import { createSlice } from '@reduxjs/toolkit';
import { searchTripadvisor } from '../dao/ArticleDAO';
import { showErrorMessage } from '@/shared/adapters/toast';
import { callUIAPI } from '../utils/apiUtils';
import { METHOD_GET } from '../utils/apiUtilsConstants';

const initialState = {
  searchResults: [],
  isSearching: false,
  error: null,
  previewContent: null,
  isPreviewLoading: false,
  previewError: null
};

const tripadvisorSlice = createSlice({
  name: 'tripadvisor',
  initialState,
  reducers: {
    searchStart: (state) => {
      state.isSearching = true;
      state.error = null;
    },
    searchSuccess: (state, action) => {
      state.searchResults = action.payload;
      state.isSearching = false;
    },
    searchFailure: (state, action) => {
      state.error = action.payload;
      state.isSearching = false;
      state.searchResults = [];
    },
    clearResults: (state) => {
      state.searchResults = [];
    },
    previewStart: (state) => {
      state.isPreviewLoading = true;
      state.previewError = null;
    },
    previewSuccess: (state, action) => {
      state.previewContent = action.payload;
      state.isPreviewLoading = false;
    },
    previewFailure: (state, action) => {
      state.previewError = action.payload;
      state.isPreviewLoading = false;
    },}
});

export const { searchStart, searchSuccess, searchFailure, clearResults, previewStart, previewSuccess, previewFailure } = tripadvisorSlice.actions;

export const performTripadvisorSearch = (query, category) => async (dispatch) => {
  if (!query) {
    showErrorMessage('Please enter a search query');
    return;
  }

  dispatch(searchStart());

  try {
    const results = await searchTripadvisor(query, category);
    
    if (results.results?.length > 0) {
      dispatch(searchSuccess(results.results));
    }
    else {
      dispatch(searchFailure('No results found'));
      showErrorMessage(`No results found for "${query}" in ${category}. Try a different search.`);
    }
  }
  catch (error) {
    dispatch(searchFailure(error.message));
    showErrorMessage(`Failed to search TripAdvisor: ${error.message}`);
  }
};

export const fetchLocationHighlights = (locationId) => async (dispatch) => {
  if (!locationId) {
    showErrorMessage('Location ID is required to fetch highlights');
    return;
  }

  dispatch(previewStart());

  try {
    const response = await callUIAPI(`tripadvisor/locationhighlights/${locationId}`,  null,  METHOD_GET,  true );
    
    if (response.data && response.data.embeddingContent) {
      dispatch(previewSuccess(response.data.embeddingContent));
    }
    else {
      dispatch(previewFailure('No embedding content found in the response'));
    }
  }
  catch (error) {
    dispatch(previewFailure(error.message || 'Failed to fetch location highlights'));
    showErrorMessage(`Failed to fetch location highlights: ${error.message || 'Unknown error'}`);
  }
};

export const selectTripadvisorResults = (state) => state.tripadvisor.searchResults;
export const selectTripadvisorIsSearching = (state) => state.tripadvisor.isSearching;
export const selectTripadvisorPreviewContent = (state) => state.tripadvisor.previewContent;
export const selectTripadvisorIsPreviewLoading = (state) => state.tripadvisor.isPreviewLoading;
export const selectTripadvisorPreviewError = (state) => state.tripadvisor.previewError;

export default tripadvisorSlice.reducer;